import { useEffect, useState, useCallback } from "react";
import { QuoteEstimatePerType } from "./quote-estimate-per-type";
import { Minus, Plus } from "lucide-react";
import { accountTypeHeaders, FIXED_TYPES, CH_TYPES, FLEX_TYPES } from "./quote-utils";

const PlanEstimates = ({ account, onUpdate, adminFee, province, onPercentageUpdate }) => {
    const isFixed = FIXED_TYPES.includes(account.accountType);
    const [benefitCategories, setBenefitCategories] = useState(account.benefitCategories);

    useEffect(() => {
        onUpdate({ ...account, benefitCategories });
    }, [benefitCategories]);

    const addNewCategory = useCallback(() => {
        const newCategory = {
            description: "",
            bencatBudget: "",
            limits: {
                single: { noOfEmployees: "", limitPerEmployee: "", limitPerEmployeeLSA: "" },
                couple: { noOfEmployees: "", limitPerEmployee: "", limitPerEmployeeLSA: "" },
                family: { noOfEmployees: "", limitPerEmployee: "", limitPerEmployeeLSA: "" },
            },
            excludeFromCoreHealth: false,
        };
        setBenefitCategories(prev => [...prev, newCategory]);
    }, []);

    const removeCategory = useCallback((index) => {
        setBenefitCategories(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleCategoryChange = useCallback((index, key, value) => {
        setBenefitCategories(prev =>
            prev.map((cat, i) => (i === index ? { ...cat, [key]: value } : cat))
        );
    }, []);

    const calculateBencatBudget = useCallback((limits) => {
        const fee = parseFloat(adminFee) || 0;

        return Object.entries(limits).reduce((total, [_, data]) => {
            const employees = parseFloat(data.noOfEmployees) || 0;
            const limit = parseFloat(data.limitPerEmployee) || 0;
            const lsaLimit = isFixed ? (parseFloat(data.limitPerEmployeeLSA) || 0) : 0;

            const baseAmount = isFixed ? limit + lsaLimit : limit;
            const subtotal = employees * baseAmount;
            const feeAmount = subtotal * (fee / 100);

            return total + subtotal + feeAmount;
        }, 0);
    }, [adminFee, isFixed]);

    const handleLimitChange = useCallback((categoryIndex, coverageLevel, field, value) => {
        setBenefitCategories(prev =>
            prev.map((cat, i) => {
                if (i !== categoryIndex) return cat;

                const updatedLimits = {
                    ...cat.limits,
                    [coverageLevel]: {
                        ...cat.limits[coverageLevel],
                        [field]: value,
                    },
                };

                return {
                    ...cat,
                    limits: updatedLimits,
                    bencatBudget: calculateBencatBudget(updatedLimits).toFixed(2),
                };
            })
        );
    }, [calculateBencatBudget]);

    return (
        <div className="mt-4">
            <hr className="border-gray-200 my-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{accountTypeHeaders[account.accountType]}</h3>
            {benefitCategories.map((category, index) => (
                <div key={index} className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Calculate by Employee Class:</p>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Job Description</th>
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Number of Employees</th>
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">{isFixed ? "HSA Limit/Employee" : "Limit/Employee"}</th>
                                    {isFixed && <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">LSA Limit/Employee</th>}
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Budget (excludes taxes)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                <tr>
                                    <td className="py-2 px-3">
                                        <select
                                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                            value={category.description}
                                            onChange={(e) => handleCategoryChange(index, "description", e.target.value)}
                                        >
                                            <option>Select</option>
                                            <option value="Executive">Executive</option>
                                            <option value="Management">Management</option>
                                            <option value="Employee Full-time">Employee Full-time</option>
                                            <option value="Employee Part-time">Employee Part-time</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </td>
                                    <td colSpan={isFixed ? "4" : "3"} className="py-2 px-3">
                                        {category.description === "Other" && (
                                            <input
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                                placeholder="Enter custom category"
                                                value={category.customCategory || ""}
                                                onChange={(e) => handleCategoryChange(index, "customCategory", e.target.value)}
                                            />
                                        )}
                                    </td>
                                </tr>
                                {["single", "couple", "family"].map((level) => (
                                    <tr key={level}>
                                        <th className="py-2 px-3 text-sm font-medium text-gray-700 bg-gray-50">{level.charAt(0).toUpperCase() + level.slice(1)}</th>
                                        <td className="py-2 px-3">
                                            <input
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                                type="number"
                                                onWheel={(e) => e.target.blur()}
                                                value={category.limits[level]?.noOfEmployees || ""}
                                                onChange={(e) => handleLimitChange(index, level, "noOfEmployees", e.target.value)}
                                            />
                                        </td>
                                        <td className="py-2 px-3">
                                            <input
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                                type="number"
                                                onWheel={(e) => e.target.blur()}
                                                value={category.limits[level]?.limitPerEmployee || ""}
                                                onChange={(e) => handleLimitChange(index, level, "limitPerEmployee", e.target.value)}
                                            />
                                        </td>
                                        {isFixed && (
                                            <td className="py-2 px-3">
                                                <input
                                                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                                    type="number"
                                                    onWheel={(e) => e.target.blur()}
                                                    value={category.limits[level]?.limitPerEmployeeLSA || ""}
                                                    onChange={(e) => handleLimitChange(index, level, "limitPerEmployeeLSA", e.target.value)}
                                                />
                                            </td>
                                        )}
                                        {level === "single" && (
                                            <td rowSpan="3" className="py-2 px-3 align-top">
                                                <input
                                                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50"
                                                    value={category.bencatBudget}
                                                    disabled
                                                />
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {CH_TYPES.includes(account.accountType) && (
                        <div className="col-sm-4 mb-3">
                            <input
                                className="form-check-input me-3"
                                type="checkbox"
                                id={`excludeFromCoreHealth-${index}`}
                                checked={category.excludeFromCoreHealth}
                                onChange={(e) => handleCategoryChange(index, "excludeFromCoreHealth", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`excludeFromCoreHealth-${index}`}>
                                Exclude from CoreHealth
                            </label>
                        </div>
                    )}
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
                            onClick={() => removeCategory(index)}
                        >
                            <Minus className="w-4 h-4" /> Remove Category
                        </button>
                        <button
                            type="button"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors"
                            onClick={addNewCategory}
                        >
                            <Plus className="w-4 h-4" /> Add Category
                        </button>
                    </div>
                </div>
            ))}
            <QuoteEstimatePerType
                planAccount={{ ...account, benefitCategories }}
                adminFee={adminFee}
                province={province}
                onPercentageUpdate={onPercentageUpdate}
            />
        </div>
    );
};

export default PlanEstimates;
